/**
 * Registrant portal: "Payments" list (check payments for current account).
 * View by row click; actions live in the details pane.
 */

import {
  REGISTRANT_PAYMENTS_ACCESSOR_KEYS,
  type RegistrantPaymentsAccessorKeys,
  getCheckColumnProperties,
} from "@/components/dashboard/payment-list-columns";
import { CheckReceivedViewSheet } from "@/components/non_registrant/check-received-view-sheet";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  type ManualCheckPaymentListItem,
  cancelRegistrantPayment,
  getRegistrantPaymentDetail,
  getRegistrantPayments,
} from "@/hera-api/manual-check-payments-api";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

export interface DashboardPaymentsProps {
  isAuthSetupComplete: boolean;
  selectedAms?: string | null;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: ReactNode;
}

export function DashboardPayments({
  isAuthSetupComplete,
  selectedAms = null,
  leadingContent,
}: DashboardPaymentsProps) {
  const queryClient = useQueryClient();
  const { canMakePayments } = useRegistrantOrgPermissions();
  const [viewManualId, setViewManualId] = useState<string | null>(null);
  const [viewProcessorId, setViewProcessorId] = useState<string | null>(null);
  const [paymentSheetCancelable, setPaymentSheetCancelable] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );

  const {
    data: paymentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["registrant-payments"],
    queryFn: getRegistrantPayments,
    enabled: isAuthSetupComplete,
  });

  const cancelMutation = useMutation({
    mutationFn: (manualTransactionId: string) =>
      cancelRegistrantPayment(manualTransactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registrant-payments"] });
      setViewManualId(null);
      setViewProcessorId(null);
      setPaymentSheetCancelable(false);
      toast({ title: "Success", description: "Payment cancelled" });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message ?? "Failed to cancel payment",
        variant: "destructive",
      });
    },
  });

  const handleRegistrantCancelPayment = useCallback(() => {
    if (!viewManualId || !paymentSheetCancelable) {
      return;
    }
    cancelMutation.mutate(viewManualId);
  }, [cancelMutation, paymentSheetCancelable, viewManualId]);

  const payments = useMemo(() => {
    const rows = paymentsData?.checks ?? [];
    if (!selectedAms) return rows;
    return rows.filter((row) => (row.ams_company_name ?? "") === selectedAms);
  }, [paymentsData?.checks, selectedAms]);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching payments..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <ErrorComponent
          title="Error loading payments"
          message={getQueryErrorMessage(error)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 w-full min-w-0">
      <DashboardTable<
        ManualCheckPaymentListItem,
        RegistrantPaymentsAccessorKeys
      >
        data={payments}
        leadingContent={
          <div className="pb-4 space-y-4">
            {leadingContent}
            <p className="text-sm text-muted-foreground">
              Select a row to view check or card payment details, download
              documents, or cancel a payment that is still awaiting your check.
            </p>
          </div>
        }
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        accessorKeys={REGISTRANT_PAYMENTS_ACCESSOR_KEYS}
        getColumnProperties={getCheckColumnProperties}
        getRowId={(row) =>
          row.manual_transaction_id ??
          row.payment_processor_transaction_id ??
          ""
        }
        tablePersistenceKey="registrant-payments"
        initialSortColumn="created_at"
        initialSortDirection="desc"
        onRowClick={(rowId) => {
          const row = payments.find(
            (r) =>
              (r.manual_transaction_id ??
                r.payment_processor_transaction_id) === rowId,
          );
          if (!row) {
            return;
          }
          setPaymentSheetCancelable(
            !!row.manual_transaction_id && row.status === "Awaiting Check",
          );
          if (row.manual_transaction_id) {
            setViewManualId(row.manual_transaction_id);
            setViewProcessorId(null);
          } else if (row.payment_processor_transaction_id) {
            setViewProcessorId(row.payment_processor_transaction_id);
            setViewManualId(null);
          }
        }}
      />

      <CheckReceivedViewSheet
        isOpen={!!viewManualId || !!viewProcessorId}
        onOpenChange={(open) => {
          if (!open) {
            setViewManualId(null);
            setViewProcessorId(null);
            setPaymentSheetCancelable(false);
          }
        }}
        manualTransactionId={viewManualId}
        paymentProcessorTransactionId={viewProcessorId}
        getManualDetail={getRegistrantPaymentDetail}
        printVariant="check"
        registrantPortalDocumentDownload
        registrantCancelEnabled={paymentSheetCancelable && canMakePayments}
        onRegistrantCancel={handleRegistrantCancelPayment}
        registrantCancelPending={cancelMutation.isPending}
      />
    </div>
  );
}
